/**
 * Discrete transformer functions for the Anthropic Messages API provider.
 * These functions convert between Standard Agent types and Anthropic API types.
 * Kept as pure functions for easy testing.
 */

import type {
  ProviderRequest,
  ProviderResponse,
  ProviderMessage,
  ProviderTool,
  ProviderToolCallPart,
  ProviderFinishReason,
  ProviderUsage,
  ProviderStreamChunk,
  ContentPart,
  ProviderReasoningDetail,
  ProviderExecutedToolResult,
} from '@standardagents/spec';
import { mapReasoningLevel } from '@standardagents/spec';
import type Anthropic from '@anthropic-ai/sdk';
import {
  calculateAnthropicUsageCost,
  extractUsageBuckets,
  type AnthropicUsageBuckets,
} from './pricing';

// ============================================================================
// Type aliases for Anthropic Messages API types
// ============================================================================

type MessageParam = Anthropic.Messages.MessageParam;
type MessageCreateParams = Anthropic.Messages.MessageCreateParams;
type ContentBlockParam = Anthropic.Messages.ContentBlockParam;
type ContentBlock = Anthropic.Messages.ContentBlock;
type ToolResultBlockParam = Anthropic.Messages.ToolResultBlockParam;
type AnthropicTool = Anthropic.Messages.Tool;
type ToolUnion = Anthropic.Messages.ToolUnion;
type ToolChoice = Anthropic.Messages.ToolChoice;
type AnthropicMessage = Anthropic.Messages.Message;
type AnthropicUsage = Anthropic.Messages.Usage;
type MessageDeltaUsage = Anthropic.Messages.MessageDeltaUsage;
type RawMessageStreamEvent = Anthropic.Messages.RawMessageStreamEvent;
type StopReason = Anthropic.Messages.StopReason;
type ImageBlockParam = Anthropic.Messages.ImageBlockParam;
type DocumentBlockParam = Anthropic.Messages.DocumentBlockParam;
type TextBlockParam = Anthropic.Messages.TextBlockParam;

/**
 * Reasoning detail formats used to round-trip Anthropic thinking blocks.
 * Thinking blocks must be replayed exactly as received (text + signature),
 * so both halves are stored on a single reasoning detail entry.
 */
export const THINKING_FORMAT = 'anthropic-thinking-v1';
export const REDACTED_THINKING_FORMAT = 'anthropic-redacted-thinking-v1';

/**
 * Default mapping from the 0-100 reasoning scale to Anthropic effort levels.
 * Used for models that support adaptive thinking + the effort parameter.
 * 'xhigh'/'max' are intentionally excluded from the default mapping since
 * support varies by model; capability-aware callers can pass their own map.
 */
export const DEFAULT_REASONING_LEVELS: Record<number, string | null> = {
  0: null,
  33: 'low',
  66: 'medium',
  100: 'high',
};

/**
 * Anthropic server-side tools. These execute on Anthropic's servers; results
 * come back as server_tool_use / *_tool_result content blocks rather than
 * client tool calls, so they are excluded from history replay.
 */
export const ANTHROPIC_NATIVE_TOOLS = new Set(['web_search']);

/**
 * Default max_tokens when the request and model capabilities don't specify
 * one. The Messages API requires max_tokens on every request.
 */
export const DEFAULT_MAX_OUTPUT_TOKENS = 8192;

// ============================================================================
// Thinking Mode Resolution
// ============================================================================

/**
 * How a Claude model expects extended thinking to be requested:
 * - 'adaptive': thinking: { type: 'adaptive' } + output_config.effort (4.6+)
 * - 'budget': thinking: { type: 'enabled', budget_tokens } (4.0-4.5, 3.7)
 * - 'none': no thinking support (Claude 3.x)
 */
export type ThinkingMode = 'adaptive' | 'budget' | 'none';

/**
 * Ordered prefix table mapping model IDs to their thinking mode.
 * More specific prefixes must come before shorter ones.
 */
const THINKING_MODE_PREFIXES: Array<[string, ThinkingMode]> = [
  ['claude-fable-5', 'adaptive'],
  ['claude-mythos', 'adaptive'],
  ['claude-opus-4-8', 'adaptive'],
  ['claude-opus-4-7', 'adaptive'],
  ['claude-opus-4-6', 'adaptive'],
  ['claude-opus-4-5', 'budget'],
  ['claude-opus-4-1', 'budget'],
  ['claude-opus-4-0', 'budget'],
  ['claude-opus-4-2', 'budget'], // dated full IDs like claude-opus-4-20250514
  ['claude-sonnet-4-6', 'adaptive'],
  ['claude-sonnet-4-5', 'budget'],
  ['claude-sonnet-4-0', 'budget'],
  ['claude-sonnet-4-2', 'budget'], // dated full IDs like claude-sonnet-4-20250514
  ['claude-haiku-4-5', 'budget'],
  ['claude-3-7-sonnet', 'budget'],
  ['claude-3', 'none'],
];

/**
 * Resolve the thinking mode for a Claude model ID.
 * Unknown (future) models default to 'adaptive' since all current-generation
 * Claude models use adaptive thinking.
 */
export function getThinkingMode(modelId: string): ThinkingMode {
  for (const [prefix, mode] of THINKING_MODE_PREFIXES) {
    if (modelId.startsWith(prefix)) {
      return mode;
    }
  }
  return 'adaptive';
}

// ============================================================================
// Input Content Transformers
// ============================================================================

/**
 * Split a data URI into media type and base64 payload.
 * Returns null when the string is not a data URI.
 */
function parseDataUri(value: string): { mediaType: string; data: string } | null {
  const match = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(value);
  if (!match) return null;
  return {
    mediaType: match[1] || 'application/octet-stream',
    data: match[3],
  };
}

type AnthropicImageMediaType = ImageBlockParam['source'] extends infer S
  ? S extends { type: 'base64'; media_type: infer M }
    ? M
    : never
  : never;

const SUPPORTED_IMAGE_MEDIA_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

function normalizeImageMediaType(mediaType: string | undefined): AnthropicImageMediaType {
  if (mediaType && SUPPORTED_IMAGE_MEDIA_TYPES.has(mediaType)) {
    return mediaType as AnthropicImageMediaType;
  }
  return 'image/png';
}

/**
 * Build an Anthropic image block from base64 data or a URL.
 */
function imageBlock(data: string, mediaType?: string): ImageBlockParam {
  if (data.startsWith('http://') || data.startsWith('https://')) {
    return { type: 'image', source: { type: 'url', url: data } };
  }
  const parsed = parseDataUri(data);
  if (parsed) {
    return {
      type: 'image',
      source: {
        type: 'base64',
        media_type: normalizeImageMediaType(parsed.mediaType),
        data: parsed.data,
      },
    };
  }
  return {
    type: 'image',
    source: {
      type: 'base64',
      media_type: normalizeImageMediaType(mediaType),
      data,
    },
  };
}

/**
 * Decode a base64 string to UTF-8 text. Used for plain-text document sources.
 */
function decodeBase64Text(data: string): string {
  try {
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
  } catch {
    return data;
  }
}

/**
 * Build an Anthropic document block (or fallback text block) from a file part.
 * PDFs map to document blocks; text files map to plain-text document sources;
 * other media types are not supported by the Messages API and fall back to a
 * descriptive text block so the model knows an attachment existed.
 */
function fileBlock(
  data: string,
  mediaType: string,
  filename?: string
): DocumentBlockParam | TextBlockParam {
  const parsed = parseDataUri(data);
  const base64 = parsed ? parsed.data : data;
  const resolvedMediaType = parsed?.mediaType || mediaType;

  if (data.startsWith('http://') || data.startsWith('https://')) {
    return { type: 'document', source: { type: 'url', url: data } };
  }

  if (resolvedMediaType === 'application/pdf') {
    return {
      type: 'document',
      source: { type: 'base64', media_type: 'application/pdf', data: base64 },
      ...(filename ? { title: filename } : {}),
    };
  }

  if (resolvedMediaType.startsWith('text/') || resolvedMediaType === 'application/json') {
    return {
      type: 'document',
      source: { type: 'text', media_type: 'text/plain', data: decodeBase64Text(base64) },
      ...(filename ? { title: filename } : {}),
    };
  }

  return {
    type: 'text',
    text: `[Unsupported file attachment${filename ? `: ${filename}` : ''} (${resolvedMediaType})]`,
  };
}

/**
 * Transform a single content part to an Anthropic content block.
 */
export function transformContentPart(part: ContentPart): ContentBlockParam {
  if (part.type === 'text') {
    return { type: 'text', text: part.text };
  }
  if (part.type === 'image') {
    return imageBlock(part.data || '', part.mediaType);
  }
  if (part.type === 'image_url') {
    return imageBlock(part.image_url?.url || '');
  }
  // File parts
  return fileBlock(part.data || '', part.mediaType || 'application/octet-stream', part.filename);
}

/**
 * Transform message content (string or parts array) to Anthropic format.
 */
export function transformMessageContent(
  content: string | ContentPart[]
): string | ContentBlockParam[] {
  if (typeof content === 'string') {
    return content;
  }
  return content.map(transformContentPart);
}

// ============================================================================
// Message Transformers
// ============================================================================

/**
 * Transform an assistant message to an Anthropic assistant MessageParam.
 * Thinking blocks (rebuilt from reasoning details) come first, then text,
 * then tool_use blocks — matching the order the API emits them.
 *
 * Returns null when the message has nothing the API can replay (e.g. an
 * assistant turn that only contained server-tool calls).
 */
export function transformAssistantMessage(
  msg: ProviderMessage & { role: 'assistant' }
): MessageParam | null {
  const content: ContentBlockParam[] = [];

  // Rebuild thinking blocks exactly as received. Details from other
  // providers (different format markers) are skipped — they can't be
  // replayed against the Anthropic API.
  if (msg.reasoningDetails) {
    for (const detail of msg.reasoningDetails) {
      if (detail.format === THINKING_FORMAT && detail.text !== undefined) {
        content.push({
          type: 'thinking',
          thinking: detail.text,
          signature: detail.data ?? '',
        });
      } else if (detail.format === REDACTED_THINKING_FORMAT && detail.data) {
        content.push({ type: 'redacted_thinking', data: detail.data });
      }
    }
  }

  if (msg.content) {
    content.push({ type: 'text', text: msg.content });
  }

  if (msg.toolCalls) {
    for (const tc of msg.toolCalls) {
      // Server-side tools (web_search) are executed by Anthropic and can't be
      // replayed as client tool_use blocks.
      if (ANTHROPIC_NATIVE_TOOLS.has(tc.name)) {
        continue;
      }
      content.push({
        type: 'tool_use',
        id: tc.id,
        name: tc.name,
        input: tc.arguments,
      });
    }
  }

  if (content.length === 0) {
    return null;
  }

  return { role: 'assistant', content };
}

/**
 * Transform a tool result message to an Anthropic tool_result block.
 * Returns null for server-side tool results (they're skipped along with
 * their tool_use blocks during replay).
 */
export function transformToolMessage(
  msg: ProviderMessage & { role: 'tool' }
): ToolResultBlockParam | null {
  if (ANTHROPIC_NATIVE_TOOLS.has(msg.toolName)) {
    return null;
  }

  let text: string;
  let isError = false;

  if (typeof msg.content === 'string') {
    text = msg.content;
  } else if ('type' in msg.content) {
    if (msg.content.type === 'text') {
      text = msg.content.text;
    } else if (msg.content.type === 'error') {
      text = msg.content.error;
      isError = true;
    } else {
      text = JSON.stringify(msg.content);
    }
  } else {
    text = JSON.stringify(msg.content);
  }

  const blocks: Array<TextBlockParam | ImageBlockParam> = [];
  if (text) {
    blocks.push({ type: 'text', text });
  }

  // Image attachments from tool execution become image blocks inside the
  // tool_result so the model can see them.
  if (msg.attachments?.length) {
    for (const attachment of msg.attachments) {
      if (attachment.type === 'image' && attachment.data) {
        blocks.push(imageBlock(attachment.data, attachment.mediaType));
      }
    }
  }

  return {
    type: 'tool_result',
    tool_use_id: msg.toolCallId,
    ...(blocks.length > 0 ? { content: blocks } : {}),
    ...(isError ? { is_error: true } : {}),
  };
}

/**
 * Transform an array of ProviderMessages to Anthropic format.
 * System messages are extracted into the top-level system prompt.
 * Consecutive tool results are merged into a single user turn, which is how
 * the Messages API expects tool results to follow parallel tool calls.
 */
export function transformMessages(messages: ProviderMessage[]): {
  anthropicMessages: MessageParam[];
  system: string | undefined;
} {
  let system: string | undefined;
  const anthropicMessages: MessageParam[] = [];
  let pendingToolResults: ToolResultBlockParam[] = [];

  const flushToolResults = () => {
    if (pendingToolResults.length > 0) {
      anthropicMessages.push({ role: 'user', content: pendingToolResults });
      pendingToolResults = [];
    }
  };

  for (const msg of messages) {
    switch (msg.role) {
      case 'system':
        system = system ? `${system}\n\n${msg.content}` : msg.content;
        break;

      case 'user':
        flushToolResults();
        anthropicMessages.push({
          role: 'user',
          content: transformMessageContent(msg.content),
        });
        break;

      case 'assistant': {
        flushToolResults();
        const assistantMessage = transformAssistantMessage(msg);
        if (assistantMessage) {
          anthropicMessages.push(assistantMessage);
        }
        break;
      }

      case 'tool': {
        const toolResult = transformToolMessage(msg);
        if (toolResult) {
          pendingToolResults.push(toolResult);
        }
        break;
      }
    }
  }

  flushToolResults();

  return { anthropicMessages, system };
}

// ============================================================================
// Tool Transformers
// ============================================================================

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Transform a single ProviderTool to an Anthropic custom tool definition.
 */
export function transformTool(tool: ProviderTool): AnthropicTool {
  const parameters = isRecord(tool.function.parameters)
    ? tool.function.parameters
    : { type: 'object', properties: {} };

  return {
    name: tool.function.name,
    ...(tool.function.description ? { description: tool.function.description } : {}),
    input_schema: {
      ...parameters,
      type: 'object',
    },
  };
}

/**
 * Transform an array of ProviderTools to Anthropic format.
 * Separates client function tools from Anthropic server-side tools.
 */
export function transformTools(tools: ProviderTool[]): ToolUnion[] {
  const result: ToolUnion[] = [];

  for (const tool of tools) {
    const toolName = tool.function.name;

    if (tool.executionMode === 'provider' && ANTHROPIC_NATIVE_TOOLS.has(toolName)) {
      if (toolName === 'web_search') {
        result.push({ type: 'web_search_20250305', name: 'web_search' });
      }
    } else {
      result.push(transformTool(tool));
    }
  }

  return result;
}

/**
 * Transform tool choice option to Anthropic format.
 * parallelToolCalls=false maps to disable_parallel_tool_use on the choice.
 */
export function transformToolChoice(
  choice: ProviderRequest['toolChoice'],
  parallelToolCalls?: boolean
): ToolChoice | undefined {
  const disable = parallelToolCalls === false ? { disable_parallel_tool_use: true } : {};

  if (choice === 'none') {
    return { type: 'none' };
  }
  if (choice === 'required') {
    return { type: 'any', ...disable };
  }
  if (typeof choice === 'object' && choice !== null && 'name' in choice) {
    return { type: 'tool', name: choice.name, ...disable };
  }
  if (choice === 'auto' || parallelToolCalls === false) {
    return { type: 'auto', ...disable };
  }
  return undefined;
}

// ============================================================================
// Response Transformers
// ============================================================================

/**
 * Map an Anthropic stop reason to a Provider finish reason.
 */
export function mapFinishReason(stopReason: StopReason | null): ProviderFinishReason {
  switch (stopReason) {
    case 'end_turn':
    case 'stop_sequence':
    case 'pause_turn':
      return 'stop';
    case 'max_tokens':
      return 'length';
    case 'tool_use':
      return 'tool_calls';
    case 'refusal':
      return 'content_filter';
    default:
      return 'stop';
  }
}

/**
 * Extract text content from response content blocks.
 */
export function extractTextContent(content: ContentBlock[]): string | null {
  const textParts: string[] = [];
  for (const block of content) {
    if (block.type === 'text') {
      textParts.push(block.text);
    }
  }
  return textParts.length > 0 ? textParts.join('') : null;
}

/**
 * Extract reasoning (thinking) content from response content blocks.
 * Each thinking block becomes one reasoning detail carrying both the text
 * and its signature so it can be replayed verbatim on the next turn.
 */
export function extractReasoningContent(content: ContentBlock[]): {
  reasoning: string | null;
  reasoningDetails: ProviderReasoningDetail[] | undefined;
} {
  let reasoning: string | null = null;
  const reasoningDetails: ProviderReasoningDetail[] = [];

  for (const block of content) {
    if (block.type === 'thinking') {
      if (block.thinking) {
        reasoning = reasoning ? `${reasoning}\n${block.thinking}` : block.thinking;
      }
      reasoningDetails.push({
        type: 'text',
        text: block.thinking,
        data: block.signature,
        format: THINKING_FORMAT,
      });
    } else if (block.type === 'redacted_thinking') {
      reasoningDetails.push({
        type: 'encrypted',
        data: block.data,
        format: REDACTED_THINKING_FORMAT,
      });
    }
  }

  return {
    reasoning,
    reasoningDetails: reasoningDetails.length > 0 ? reasoningDetails : undefined,
  };
}

/**
 * Extract client tool calls from response content blocks.
 */
export function extractToolCalls(
  content: ContentBlock[]
): ProviderToolCallPart[] | undefined {
  const toolCalls: ProviderToolCallPart[] = [];

  for (const block of content) {
    if (block.type === 'tool_use') {
      toolCalls.push({
        id: block.id,
        name: block.name,
        arguments: isRecord(block.input) ? block.input : {},
      });
    }
  }

  return toolCalls.length > 0 ? toolCalls : undefined;
}

function webSearchToolResult(
  id: string,
  input: unknown,
  resultContent: unknown
): ProviderExecutedToolResult {
  const query = isRecord(input) && typeof input.query === 'string' ? input.query : undefined;

  let status: ProviderExecutedToolResult['status'] = 'completed';
  let sources: Array<{ type?: string; url: string; title?: string }> | undefined;

  if (Array.isArray(resultContent)) {
    sources = resultContent
      .filter(isRecord)
      .filter((item) => item.type === 'web_search_result' && typeof item.url === 'string')
      .map((item) => ({
        type: 'url',
        url: item.url as string,
        ...(typeof item.title === 'string' ? { title: item.title } : {}),
      }));
  } else if (isRecord(resultContent) && resultContent.type === 'web_search_tool_result_error') {
    status = 'failed';
  }

  return {
    name: 'web_search',
    type: 'web_search',
    provider: 'anthropic',
    id,
    status,
    result: {
      actions: [{
        type: 'search',
        ...(query ? { query } : {}),
        ...(sources && sources.length > 0 ? { sources } : {}),
      }],
    },
  };
}

/**
 * Extract server-side tool executions (web search) from response content
 * blocks, pairing server_tool_use blocks with their result blocks.
 */
export function extractProviderToolResults(
  content: ContentBlock[]
): ProviderExecutedToolResult[] | undefined {
  const providerTools: ProviderExecutedToolResult[] = [];
  const serverToolUses = new Map<string, { name: string; input: unknown }>();

  for (const block of content) {
    if (block.type === 'server_tool_use') {
      serverToolUses.set(block.id, { name: block.name, input: block.input });
    } else if (block.type === 'web_search_tool_result') {
      const use = serverToolUses.get(block.tool_use_id);
      providerTools.push(
        webSearchToolResult(block.tool_use_id, use?.input, block.content)
      );
    }
  }

  return providerTools.length > 0 ? providerTools : undefined;
}

/**
 * Transform Anthropic usage to Provider usage format.
 *
 * Anthropic's input_tokens excludes cached tokens, while the Standard Agents
 * convention (matching OpenAI) is that promptTokens covers the full prompt
 * with cachedTokens as the subset read from cache. The buckets are summed
 * here so usage totals stay comparable across providers.
 *
 * When the model has known pricing, the exact dollar cost is computed from
 * the raw billing buckets (cache writes at 1.25x/2x, cache reads at 0.1x) —
 * the API itself reports tokens only, never cost.
 */
export function transformUsage(
  usage: AnthropicUsage | undefined,
  modelId?: string
): ProviderUsage {
  if (!usage) {
    return { promptTokens: 0, completionTokens: 0, totalTokens: 0, provider: 'anthropic' };
  }

  const cacheCreation = usage.cache_creation_input_tokens ?? 0;
  const cacheRead = usage.cache_read_input_tokens ?? 0;
  const promptTokens = usage.input_tokens + cacheCreation + cacheRead;
  const completionTokens = usage.output_tokens;
  const reasoningTokens = usage.output_tokens_details?.thinking_tokens;
  const cost = modelId
    ? calculateAnthropicUsageCost(modelId, extractUsageBuckets(usage))
    : undefined;

  return {
    promptTokens,
    completionTokens,
    totalTokens: promptTokens + completionTokens,
    ...(reasoningTokens !== undefined ? { reasoningTokens } : {}),
    ...(cacheRead > 0 ? { cachedTokens: cacheRead } : {}),
    ...(cost !== undefined ? { cost } : {}),
    provider: 'anthropic',
  };
}

/**
 * Transform a complete Anthropic Message response to ProviderResponse.
 */
export function transformResponse(message: AnthropicMessage): ProviderResponse {
  const content = extractTextContent(message.content);
  const { reasoning, reasoningDetails } = extractReasoningContent(message.content);
  const toolCalls = extractToolCalls(message.content);
  const providerTools = extractProviderToolResults(message.content);

  return {
    content,
    reasoning,
    reasoningDetails,
    toolCalls,
    providerTools,
    finishReason: mapFinishReason(message.stop_reason),
    usage: transformUsage(message.usage, message.model),
    metadata: {
      model: message.model,
      provider: 'anthropic',
      requestId: message.id,
      ...(message.stop_sequence ? { stopSequence: message.stop_sequence } : {}),
      ...(message.usage?.cache_creation_input_tokens
        ? { cacheCreationInputTokens: message.usage.cache_creation_input_tokens }
        : {}),
      ...(message.usage?.cache_read_input_tokens
        ? { cacheReadInputTokens: message.usage.cache_read_input_tokens }
        : {}),
      ...(message.usage?.service_tier ? { serviceTier: message.usage.service_tier } : {}),
      ...(message.usage?.server_tool_use
        ? { serverToolUse: { ...message.usage.server_tool_use } }
        : {}),
    },
  };
}

// ============================================================================
// Request Builder
// ============================================================================

/**
 * Validate a providerOptions thinking override into the SDK's config shape.
 * Unknown shapes are ignored so a bad option can't produce a malformed request.
 */
function resolveThinkingOverride(
  value: unknown
): Anthropic.Messages.ThinkingConfigParam | undefined {
  if (!isRecord(value)) return undefined;

  const display =
    value.display === 'summarized' || value.display === 'omitted'
      ? value.display
      : undefined;

  if (value.type === 'adaptive') {
    return { type: 'adaptive', ...(display ? { display } : {}) };
  }
  if (value.type === 'enabled' && typeof value.budget_tokens === 'number') {
    return {
      type: 'enabled',
      budget_tokens: value.budget_tokens,
      ...(display ? { display } : {}),
    };
  }
  if (value.type === 'disabled') {
    return { type: 'disabled' };
  }
  return undefined;
}

/**
 * Clamp a thinking budget to the valid range: >= 1024 and < maxTokens.
 * Returns null when there isn't enough output headroom for thinking.
 */
function resolveThinkingBudget(
  level: number,
  maxTokens: number,
  explicitBudget?: number
): number | null {
  if (maxTokens < 2048) {
    return null;
  }
  const ceiling = maxTokens - 1024;
  const scaled = explicitBudget ?? Math.floor((maxTokens * Math.min(level, 100)) / 100 * 0.75);
  return Math.max(1024, Math.min(scaled, ceiling));
}

/**
 * Build a Messages API create request from a ProviderRequest.
 *
 * Thinking configuration is derived from the request's 0-100 reasoning level
 * and the model's thinking mode: adaptive models get
 * `thinking: { type: 'adaptive' }` plus an effort level, while
 * budget-mode models get `thinking: { type: 'enabled', budget_tokens }`.
 * When thinking is active, sampling parameters are omitted — the API rejects
 * temperature/top_p/top_k alongside thinking.
 */
export function buildCreateParams(
  request: ProviderRequest,
  reasoningLevels: Record<number, string | null> = DEFAULT_REASONING_LEVELS,
  defaultMaxTokens: number = DEFAULT_MAX_OUTPUT_TOKENS
): MessageCreateParams {
  const { anthropicMessages, system } = transformMessages(request.messages);

  const maxTokens = request.maxOutputTokens ?? defaultMaxTokens;

  const params: MessageCreateParams = {
    model: request.model,
    max_tokens: maxTokens,
    messages: anthropicMessages,
  };

  // Provider options that this builder manages itself.
  const providerOptions = isRecord(request.providerOptions) ? request.providerOptions : {};
  const {
    cacheSystemPrompt,
    thinking: thinkingOverride,
    ...passthroughOptions
  } = providerOptions;

  let systemText = system;

  // Response format: structured outputs via output_config when a schema is
  // provided; otherwise fall back to a system prompt instruction since the
  // Messages API has no bare JSON mode.
  if (request.responseFormat?.type === 'json') {
    if (request.responseFormat.schema) {
      params.output_config = {
        format: { type: 'json_schema', schema: request.responseFormat.schema },
      };
    } else {
      const jsonInstruction = 'Respond with valid JSON only. Do not include any text outside the JSON.';
      systemText = systemText ? `${systemText}\n\n${jsonInstruction}` : jsonInstruction;
    }
  }

  if (systemText) {
    params.system = cacheSystemPrompt === true
      ? [{ type: 'text', text: systemText, cache_control: { type: 'ephemeral' } }]
      : systemText;
  }

  // Tools
  if (request.tools && request.tools.length > 0) {
    params.tools = transformTools(request.tools);
  }
  const toolChoice = transformToolChoice(request.toolChoice, request.parallelToolCalls);
  if (toolChoice && params.tools && params.tools.length > 0) {
    params.tool_choice = toolChoice;
  }

  // Thinking configuration
  const thinkingMode = getThinkingMode(request.model);
  let thinkingEnabled = false;

  const resolvedOverride = resolveThinkingOverride(thinkingOverride);
  if (resolvedOverride) {
    params.thinking = resolvedOverride;
    thinkingEnabled = resolvedOverride.type !== 'disabled';
  } else if (request.reasoning?.level !== undefined && request.reasoning.level > 0) {
    if (thinkingMode === 'adaptive') {
      params.thinking = {
        type: 'adaptive',
        ...(request.reasoning.exclude ? { display: 'omitted' } : {}),
      };
      const effort = mapReasoningLevel(request.reasoning.level, reasoningLevels);
      if (effort) {
        params.output_config = {
          ...params.output_config,
          effort: effort as 'low' | 'medium' | 'high' | 'xhigh' | 'max',
        };
      }
      thinkingEnabled = true;
    } else if (thinkingMode === 'budget') {
      const budget = resolveThinkingBudget(
        request.reasoning.level,
        maxTokens,
        request.reasoning.maxTokens
      );
      if (budget !== null) {
        params.thinking = {
          type: 'enabled',
          budget_tokens: budget,
          ...(request.reasoning.exclude ? { display: 'omitted' } : {}),
        };
        thinkingEnabled = true;
      }
    }
    // thinkingMode 'none': Claude 3.x — no thinking parameter
  }

  // Sampling parameters are incompatible with thinking (the API requires
  // temperature 1 and rejects top_p/top_k when thinking is on), and the
  // newest models reject them entirely — only forward when thinking is off.
  if (!thinkingEnabled) {
    if (request.temperature !== undefined) {
      params.temperature = request.temperature;
    }
    if (request.topP !== undefined) {
      params.top_p = request.topP;
    }
    if (request.topK !== undefined) {
      params.top_k = request.topK;
    }
  }

  if (request.stopSequences && request.stopSequences.length > 0) {
    params.stop_sequences = request.stopSequences;
  }

  // Merge remaining provider-specific options (service_tier, metadata, ...).
  Object.assign(params, passthroughOptions);

  return params;
}

// ============================================================================
// Stream State and Chunk Transformers
// ============================================================================

interface StreamToolCall {
  id: string;
  name: string;
  argumentsJson: string;
}

interface StreamThinkingBlock {
  thinking: string;
  signature: string;
}

interface StreamServerToolUse {
  id: string;
  name: string;
  inputJson: string;
}

export interface StreamState {
  /** Open content blocks keyed by stream index */
  toolCallsByIndex: Map<number, StreamToolCall>;
  thinkingByIndex: Map<number, StreamThinkingBlock>;
  serverToolsByIndex: Map<number, StreamServerToolUse>;
  /** Completed server tool uses awaiting their result block */
  pendingServerTools: Map<string, { name: string; input: unknown }>;
  reasoningDetails: ProviderReasoningDetail[];
  hasContent: boolean;
  hasReasoning: boolean;
  /** Usage captured from message_start (input/cache buckets) */
  usage: ProviderUsage;
  /** Raw billing buckets for exact cost calculation at message_stop */
  buckets: AnthropicUsageBuckets;
  finishReason: ProviderFinishReason;
  model: string | null;
  messageId: string | null;
  serverToolUse: { web_search_requests: number; web_fetch_requests: number } | null;
}

export function createStreamState(): StreamState {
  return {
    toolCallsByIndex: new Map(),
    thinkingByIndex: new Map(),
    serverToolsByIndex: new Map(),
    pendingServerTools: new Map(),
    reasoningDetails: [],
    hasContent: false,
    hasReasoning: false,
    usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0, provider: 'anthropic' },
    buckets: {
      inputTokens: 0,
      outputTokens: 0,
      cacheReadTokens: 0,
      cacheWrite5mTokens: 0,
      cacheWrite1hTokens: 0,
    },
    finishReason: 'stop',
    model: null,
    messageId: null,
    serverToolUse: null,
  };
}

function parseToolCallArguments(argumentsJson: string): Record<string, unknown> {
  try {
    const parsed: unknown = argumentsJson ? JSON.parse(argumentsJson) : {};
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function applyDeltaUsage(state: StreamState, usage: MessageDeltaUsage): void {
  const cacheCreation = usage.cache_creation_input_tokens ?? 0;
  const cacheRead = usage.cache_read_input_tokens ?? 0;
  // message_delta usage is cumulative; input buckets repeat message_start
  // values when present, so prefer them when provided.
  if (usage.input_tokens !== null && usage.input_tokens !== undefined) {
    state.usage.promptTokens = usage.input_tokens + cacheCreation + cacheRead;
    if (cacheRead > 0) {
      state.usage.cachedTokens = cacheRead;
    }
    state.buckets.inputTokens = usage.input_tokens;
    state.buckets.cacheReadTokens = cacheRead;
    // The delta has no per-TTL cache-write breakdown; keep the 1h split from
    // message_start and attribute the remainder to the 5-minute bucket.
    state.buckets.cacheWrite5mTokens = Math.max(
      cacheCreation - state.buckets.cacheWrite1hTokens,
      0
    );
  }
  state.buckets.outputTokens = usage.output_tokens;
  state.usage.completionTokens = usage.output_tokens;
  const thinkingTokens = usage.output_tokens_details?.thinking_tokens;
  if (thinkingTokens !== undefined) {
    state.usage.reasoningTokens = thinkingTokens;
  }
  state.usage.totalTokens = state.usage.promptTokens + state.usage.completionTokens;
  if (usage.server_tool_use) {
    state.serverToolUse = { ...usage.server_tool_use };
  }
}

/**
 * Process an Anthropic stream event and return provider stream chunks.
 */
export function processStreamEvent(
  event: RawMessageStreamEvent,
  state: StreamState
): ProviderStreamChunk[] {
  const chunks: ProviderStreamChunk[] = [];

  switch (event.type) {
    case 'message_start': {
      const usage = event.message.usage;
      const cacheCreation = usage.cache_creation_input_tokens ?? 0;
      const cacheRead = usage.cache_read_input_tokens ?? 0;
      state.usage.promptTokens = usage.input_tokens + cacheCreation + cacheRead;
      if (cacheRead > 0) {
        state.usage.cachedTokens = cacheRead;
      }
      state.usage.completionTokens = usage.output_tokens;
      state.usage.totalTokens = state.usage.promptTokens + state.usage.completionTokens;
      state.buckets = extractUsageBuckets(usage);
      state.model = event.message.model;
      state.messageId = event.message.id;
      break;
    }

    case 'content_block_start': {
      const block = event.content_block;
      if (block.type === 'tool_use') {
        state.toolCallsByIndex.set(event.index, {
          id: block.id,
          name: block.name,
          argumentsJson: '',
        });
        chunks.push({ type: 'tool-call-start', id: block.id, name: block.name });
      } else if (block.type === 'thinking') {
        state.thinkingByIndex.set(event.index, {
          thinking: block.thinking || '',
          signature: block.signature || '',
        });
      } else if (block.type === 'redacted_thinking') {
        state.reasoningDetails.push({
          type: 'encrypted',
          data: block.data,
          format: REDACTED_THINKING_FORMAT,
        });
      } else if (block.type === 'server_tool_use') {
        state.serverToolsByIndex.set(event.index, {
          id: block.id,
          name: block.name,
          inputJson: '',
        });
      } else if (block.type === 'web_search_tool_result') {
        const use = state.pendingServerTools.get(block.tool_use_id);
        chunks.push({
          type: 'provider-tool-done',
          tool: webSearchToolResult(block.tool_use_id, use?.input, block.content),
        });
        state.pendingServerTools.delete(block.tool_use_id);
      }
      break;
    }

    case 'content_block_delta': {
      const delta = event.delta;
      if (delta.type === 'text_delta') {
        state.hasContent = true;
        chunks.push({ type: 'content-delta', delta: delta.text });
      } else if (delta.type === 'thinking_delta') {
        state.hasReasoning = true;
        const thinkingBlock = state.thinkingByIndex.get(event.index);
        if (thinkingBlock) {
          thinkingBlock.thinking += delta.thinking;
        }
        chunks.push({ type: 'reasoning-delta', delta: delta.thinking });
      } else if (delta.type === 'signature_delta') {
        const thinkingBlock = state.thinkingByIndex.get(event.index);
        if (thinkingBlock) {
          thinkingBlock.signature += delta.signature;
        }
      } else if (delta.type === 'input_json_delta') {
        const toolCall = state.toolCallsByIndex.get(event.index);
        if (toolCall) {
          toolCall.argumentsJson += delta.partial_json;
          chunks.push({
            type: 'tool-call-delta',
            id: toolCall.id,
            argumentsDelta: delta.partial_json,
          });
        } else {
          const serverTool = state.serverToolsByIndex.get(event.index);
          if (serverTool) {
            serverTool.inputJson += delta.partial_json;
          }
        }
      }
      break;
    }

    case 'content_block_stop': {
      const toolCall = state.toolCallsByIndex.get(event.index);
      if (toolCall) {
        chunks.push({
          type: 'tool-call-done',
          id: toolCall.id,
          arguments: parseToolCallArguments(toolCall.argumentsJson),
        });
        state.toolCallsByIndex.delete(event.index);
        break;
      }

      const thinkingBlock = state.thinkingByIndex.get(event.index);
      if (thinkingBlock) {
        state.reasoningDetails.push({
          type: 'text',
          text: thinkingBlock.thinking,
          data: thinkingBlock.signature,
          format: THINKING_FORMAT,
        });
        state.thinkingByIndex.delete(event.index);
        break;
      }

      const serverTool = state.serverToolsByIndex.get(event.index);
      if (serverTool) {
        state.pendingServerTools.set(serverTool.id, {
          name: serverTool.name,
          input: parseToolCallArguments(serverTool.inputJson),
        });
        state.serverToolsByIndex.delete(event.index);
      }
      break;
    }

    case 'message_delta': {
      if (event.delta.stop_reason) {
        state.finishReason = mapFinishReason(event.delta.stop_reason);
      }
      applyDeltaUsage(state, event.usage);
      break;
    }

    case 'message_stop': {
      if (state.hasContent) {
        chunks.push({ type: 'content-done' });
      }
      if (state.hasReasoning) {
        chunks.push({ type: 'reasoning-done' });
      }
      const cost = calculateAnthropicUsageCost(state.model, state.buckets);
      chunks.push({
        type: 'finish',
        finishReason: state.finishReason,
        usage: { ...state.usage, ...(cost !== undefined ? { cost } : {}) },
        ...(state.reasoningDetails.length > 0
          ? { reasoningDetails: [...state.reasoningDetails] }
          : {}),
      });
      break;
    }

    default:
      break;
  }

  return chunks;
}

/**
 * Create error chunk.
 */
export function createErrorChunk(error: string, code?: string): ProviderStreamChunk {
  return { type: 'error', error, code };
}

// ============================================================================
// Inspection Utilities
// ============================================================================

/**
 * Check if a string looks like base64 data that should be truncated.
 */
function isBase64Like(str: string): boolean {
  if (str.startsWith('data:')) return true;

  if (str.length > 200) {
    const base64Pattern = /^[A-Za-z0-9+/]+=*$/;
    return base64Pattern.test(str.substring(0, 200));
  }

  return false;
}

function truncateBase64String(str: string, maxLength: number = 50): string {
  if (str.length <= maxLength) return str;

  const preview = str.substring(0, maxLength);
  return `${preview}...[truncated, ${str.length.toLocaleString()} chars]`;
}

/**
 * Recursively truncate base64 data in an object for inspection.
 */
export function truncateBase64<T>(obj: T, maxLength: number = 50): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    if (isBase64Like(obj)) {
      return truncateBase64String(obj, maxLength) as T;
    }
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => truncateBase64(item, maxLength)) as T;
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = truncateBase64(value, maxLength);
    }
    return result as T;
  }

  return obj;
}
